import { IGlobalOrganizationResponse, IRiderMeResponse } from '@sparelabs/api-client'
import { CaseTypeStatus, ICaseResponse, ICaseStatusResponse, ICaseTypeResponse } from '@sparelabs/engage-client'
import React from 'react'
import { useAsync } from 'react-use'
import { ApiClientBuilder, EngageClientBuilder } from 'src/api/ApiClientBuilders'
import { ErrorView, FormContainer, LoadingView } from 'src/components'
import { FloatingMenu } from 'src/components/buttons/FloatingMenu'
import { CaseCard } from 'src/components/fields/CaseCard'
import { st } from 'src/locales/TranslationHelper'
import { useFormContext } from 'src/pages/Form/FormContext'
import { FormSubmitted } from 'src/pages/Form/FormStepTypes'
import styled from 'styled-components'

const CaseSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const CaseSelectStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`

const SelectContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 8px;
`

const RiderCasesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

const TitleContainer = styled.p`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 0px;
`

const InfoContainer = styled.p`
  font-size: 15px;
  margin-bottom: 0px;
`

const CaseTitleContainer = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 0px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

interface ICaseSelectData {
  rider: IRiderMeResponse
  cases: ICaseResponse[]
  caseStatuses: Map<string, ICaseStatusResponse[]>
  caseTypes: ICaseTypeResponse[]
}

// should display cases assigned to this rider and allow them to create new cases
export const CaseSelectStep = (): JSX.Element => {
  const { setCaseForm, setForm, onNextStep, organization } = useFormContext()
  const { value, loading, error } = useAsync(async () => {
    const client = ApiClientBuilder.build()
    const rider = await client.users.getMeRider()

    const engageClient = EngageClientBuilder.build()
    const cases = await engageClient.cases.list({ userId: rider.id, isArchived: false, limit: 50 })
    const uniqueCaseTypeIds = cases.data.reduce<string[]>((acc, caseData) => {
      if (!acc.includes(caseData.caseTypeId)) {
        acc.push(caseData.caseTypeId)
      }
      return acc
    }, [])

    const caseStatusMap = new Map<string, ICaseStatusResponse[]>()
    for (const caseTypeId of uniqueCaseTypeIds) {
      const statuses = await engageClient.caseStatuses.list({ caseTypeId, limit: 50 })
      caseStatusMap.set(caseTypeId, statuses.data)
    }

    const caseTypes = await engageClient.caseTypes.list({
      status: CaseTypeStatus.Active,
      riderInterfaceVisibility: true,
    })

    return {
      rider,
      cases: cases.data,
      caseStatuses: caseStatusMap,
      caseTypes: caseTypes.data.filter((caseType) => caseType.defaultFormKey),
    }
  }, [])

  if (loading) {
    return <LoadingView />
  }

  if (error || !value) {
    return <ErrorView message={error?.message || st.errors.noContent()} />
  }

  const choices = value
    ? value.caseTypes.map((caseType: ICaseTypeResponse) => ({
        key: caseType.key,
        label: caseType.name,
      }))
    : []

  return (
    <FormContainer>
      <CaseSelectStepContainer>
        <WelcomeInfoContainer rider={value?.rider} organization={organization} />
        <CaseSelectContainer>
          <SelectContainer>
            <CaseTitleContainer>{st.forms.caseSelect.title()}</CaseTitleContainer>
            <FloatingMenu
              icon='plus'
              disabled={value?.caseTypes.length === 0}
              disabledHint={st.forms.caseSelect.disabledHint()}
              title={st.forms.caseSelect.createCaseButton()}
              options={choices}
              onClick={async (key: string) => {
                if (!value) {
                  throw new Error('No case types found')
                }

                const { defaultForm } = await fetchData(key, organization, value)

                setForm(defaultForm)
                setCaseForm(undefined, FormSubmitted.Never)
                onNextStep()
              }}
              alignRight={true}
            />
          </SelectContainer>
          <RiderCasesContainer>
            {value?.cases.map((caseData, key) => (
              <CaseCard caseData={caseData} caseStatuses={value.caseStatuses} caseTypes={value.caseTypes} key={key} />
            ))}
          </RiderCasesContainer>
        </CaseSelectContainer>
      </CaseSelectStepContainer>
    </FormContainer>
  )
}

const WelcomeInfoContainer = (props: {
  rider: IRiderMeResponse
  organization: IGlobalOrganizationResponse
}): JSX.Element => (
  <div>
    <TitleContainer>{st.forms.caseSelect.welcomeMessage({ firstName: props.rider.firstName ?? '' })}</TitleContainer>
    <InfoContainer>{st.forms.caseSelect.infoPrompt({ orgName: props.organization.name })}</InfoContainer>
  </div>
)

const fetchData = async (key: string, organization: IGlobalOrganizationResponse, value: ICaseSelectData) => {
  const engageClient = EngageClientBuilder.build()
  const caseType = value?.caseTypes.find((caseType) => caseType.key === key)

  if (!caseType || !value?.rider) {
    throw new Error('Case type not found')
  }

  if (!caseType?.defaultFormKey) {
    throw new Error('No default form found')
  }

  const form = await engageClient.public.getForm({
    organizationId: organization.id,
    caseTypeKey: key,
    formKey: caseType?.defaultFormKey ?? '',
  })

  return { defaultForm: form }
}
